/**
 * The theme control (`OD-12`, answered by the product owner: light *and* dark
 * ship from the start, because retrofitting dark across shadow roots is
 * invasive).
 *
 * How the three states map onto CSS, and why that is all the JavaScript there
 * is:
 *
 * | Preference | `:root[data-theme]` | Result |
 * |---|---|---|
 * | `auto`  | attribute **removed** | `prefers-color-scheme` decides, with no script involved |
 * | `light` | `light`               | wins over a dark OS preference |
 * | `dark`  | `dark`                | wins over a light OS preference |
 *
 * `shared/tokens.css` is written so the explicit attribute wins in **both**
 * directions, and it does: the dark block is guarded by
 * `:root:not([data-theme='light'])` inside the media query, so a dark-preference
 * OS cannot override an explicit light; and the standalone `:root[data-theme='dark']`
 * block is duplicated *after* the media query at equal specificity, so it wins
 * on a light-preference OS by source order. `auto` therefore needs no rule of
 * its own — removing the attribute restores the default cascade exactly.
 *
 * Because `auto` is the default and needs no script, a first paint before this
 * module runs already shows the right theme for most users. Only an explicit
 * override has to be applied, and it is applied at module evaluation — before
 * the app shell's first render — so there is no flash.
 */

import { LitElement, css, html } from "lit";
import { customElement, state } from "lit/decorators.js";

/** ThemePreference is the stored choice. `auto` means "follow the OS". */
export type ThemePreference = "light" | "dark" | "auto";

/** The localStorage key. Namespaced so it cannot collide with the auth store. */
const STORAGE_KEY = "sluice-cloud.theme";

/** The OS-preference query, kept as one shared MediaQueryList so every listener
 * observes the same object. */
const darkQuery = window.matchMedia("(prefers-color-scheme: dark)");

/** Event dispatched on `window` whenever the preference or the resolved theme
 * changes, so any component can react without polling. */
const THEME_EVENT = "sluice-theme-change";

/**
 * getThemePreference reads the stored preference, defaulting to `auto`. A
 * storage read can throw in a hardened browser profile (or when cookies are
 * blocked for the origin); the theme is cosmetic, so a failure degrades to
 * `auto` rather than breaking the boot.
 */
export function getThemePreference(): ThemePreference {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw === "light" || raw === "dark" || raw === "auto") return raw;
  } catch {
    // Storage unavailable — fall through to the default.
  }
  return "auto";
}

/**
 * setThemePreference persists the choice and applies it immediately.
 *
 *   setThemePreference("dark");
 */
export function setThemePreference(preference: ThemePreference): void {
  try {
    if (preference === "auto") {
      localStorage.removeItem(STORAGE_KEY);
    } else {
      localStorage.setItem(STORAGE_KEY, preference);
    }
  } catch {
    // Not persisting is survivable; not applying is not. Carry on.
  }
  applyTheme(preference);
  window.dispatchEvent(new CustomEvent(THEME_EVENT));
}

/** resolvedTheme reports which of the two themes is actually rendering — the
 * preference, or what the OS says when the preference is `auto`. */
export function resolvedTheme(): "light" | "dark" {
  const preference = getThemePreference();
  if (preference !== "auto") return preference;
  return darkQuery.matches ? "dark" : "light";
}

/**
 * applyTheme writes (or removes) `data-theme` on the document element. Exported
 * for tests and for the boot path; ordinary callers use [setThemePreference],
 * which persists as well.
 */
export function applyTheme(preference: ThemePreference): void {
  const root = document.documentElement;
  if (preference === "auto") {
    root.removeAttribute("data-theme");
  } else {
    root.setAttribute("data-theme", preference);
  }
}

/** onThemeChange subscribes to preference and OS changes, and returns an
 * unsubscribe. Components use it to re-render a resolved-theme indicator. */
export function onThemeChange(listener: () => void): () => void {
  window.addEventListener(THEME_EVENT, listener);
  darkQuery.addEventListener("change", listener);
  return () => {
    window.removeEventListener(THEME_EVENT, listener);
    darkQuery.removeEventListener("change", listener);
  };
}

// Apply the stored preference at module evaluation. main.ts imports this module
// immediately after the token sheet, so the attribute is on <html> before the
// app shell's first render.
applyTheme(getThemePreference());

/** The three states in display order, with their labels and glyph paths. */
const OPTIONS: ReadonlyArray<{ value: ThemePreference; label: string; path: string }> = [
  {
    value: "light",
    label: "Light",
    // A sun: filled disc plus eight rays.
    path: "M8 5.2a2.8 2.8 0 1 0 0 5.6 2.8 2.8 0 0 0 0-5.6Zm0-4a.7.7 0 0 1 .7.7v1.3a.7.7 0 0 1-1.4 0V1.9a.7.7 0 0 1 .7-.7Zm0 11.3a.7.7 0 0 1 .7.7v1.3a.7.7 0 0 1-1.4 0v-1.3a.7.7 0 0 1 .7-.7ZM1.9 7.3h1.3a.7.7 0 0 1 0 1.4H1.9a.7.7 0 0 1 0-1.4Zm10.9 0h1.3a.7.7 0 0 1 0 1.4h-1.3a.7.7 0 0 1 0-1.4ZM3.3 2.9l.9.9a.7.7 0 1 1-1 1l-.9-.9a.7.7 0 0 1 1-1Zm8.5 8.5.9.9a.7.7 0 0 1-1 1l-.9-.9a.7.7 0 0 1 1-1Zm1.9-8.5a.7.7 0 0 1 0 1l-.9.9a.7.7 0 1 1-1-1l.9-.9a.7.7 0 0 1 1 0ZM4.2 11.4a.7.7 0 0 1 0 1l-.9.9a.7.7 0 0 1-1-1l.9-.9a.7.7 0 0 1 1 0Z",
  },
  {
    value: "dark",
    label: "Dark",
    // A crescent moon.
    path: "M13.4 10.3A5.9 5.9 0 0 1 5.7 2.6a.7.7 0 0 0-.9-.9 6.9 6.9 0 1 0 9.5 9.5.7.7 0 0 0-.9-.9Z",
  },
  {
    value: "auto",
    label: "Auto",
    // A disc split down the middle: half light, half dark.
    path: "M8 1a7 7 0 1 0 0 14A7 7 0 0 0 8 1Zm0 1.6v10.8a5.4 5.4 0 0 1 0-10.8Z",
  },
];

/**
 * sluice-theme-toggle is the three-state theme control for the app chrome:
 * light, dark, or follow the operating system. The choice persists in
 * localStorage and is applied to `:root` as `data-theme`, which pierces every
 * shadow root because the design tokens are custom properties.
 *
 * It has no properties: the preference is global state, so an element that took
 * one as an attribute would immediately be a second source of truth. Read and
 * write it with [getThemePreference] / [setThemePreference] instead.
 *
 * Keyboard: it is a `radiogroup` with roving tabindex — Tab enters at the
 * selected option, arrow keys move and select, Home and End jump to the ends.
 *
 *   <sluice-theme-toggle></sluice-theme-toggle>
 */
@customElement("sluice-theme-toggle")
export class SluiceThemeToggle extends LitElement {
  @state() private preference: ThemePreference = getThemePreference();

  private unsubscribe?: () => void;

  static override styles = css`
    :host {
      display: inline-block;
    }
    div[role="radiogroup"] {
      display: inline-flex;
      align-items: center;
      gap: 2px;
      padding: 2px;
      border: 1px solid var(--bino-border);
      border-radius: var(--bino-radius-pill);
      background: var(--bino-surface-subtle);
    }
    button {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      width: 1.75rem;
      height: 1.75rem;
      padding: 0;
      border: none;
      border-radius: var(--bino-radius-pill);
      background: transparent;
      color: var(--bino-text-secondary);
      cursor: pointer;
      transition:
        background var(--bino-transition-fast),
        color var(--bino-transition-fast);
    }
    button:hover {
      color: var(--bino-text);
      background: var(--bino-surface-hover);
    }
    button:focus-visible {
      outline: none;
      box-shadow: 0 0 0 3px var(--bino-focus-ring);
    }
    button[aria-checked="true"] {
      color: var(--bino-on-primary);
      background: var(--bino-primary);
    }
    button[aria-checked="true"]:hover {
      background: var(--bino-primary-hover);
    }
    svg {
      width: 1rem;
      height: 1rem;
      display: block;
    }
  `;

  override connectedCallback() {
    super.connectedCallback();
    // The OS half of the subscription matters even though `auto` needs no
    // script: the title text reports which theme `auto` currently resolves to.
    this.unsubscribe = onThemeChange(() => {
      this.preference = getThemePreference();
      this.requestUpdate();
    });
  }

  override disconnectedCallback() {
    super.disconnectedCallback();
    this.unsubscribe?.();
    this.unsubscribe = undefined;
  }

  private select(value: ThemePreference) {
    setThemePreference(value);
    this.preference = value;
  }

  /** Arrow keys move the selection and the focus together, which is the
   * radiogroup convention: moving through a radio group selects as it goes. */
  private onKeyDown(e: KeyboardEvent) {
    const index = OPTIONS.findIndex((o) => o.value === this.preference);
    let next = index;
    switch (e.key) {
      case "ArrowRight":
      case "ArrowDown":
        next = (index + 1) % OPTIONS.length;
        break;
      case "ArrowLeft":
      case "ArrowUp":
        next = (index - 1 + OPTIONS.length) % OPTIONS.length;
        break;
      case "Home":
        next = 0;
        break;
      case "End":
        next = OPTIONS.length - 1;
        break;
      default:
        return;
    }
    e.preventDefault();
    const option = OPTIONS[next];
    if (!option) return;
    this.select(option.value);
    void this.updateComplete.then(() => {
      this.renderRoot.querySelector<HTMLButtonElement>('button[aria-checked="true"]')?.focus();
    });
  }

  override render() {
    const resolved = resolvedTheme();
    return html`
      <div role="radiogroup" aria-label="Colour theme" @keydown=${this.onKeyDown}>
        ${OPTIONS.map((option) => {
          const checked = option.value === this.preference;
          const title =
            option.value === "auto"
              ? `Auto — following your system, currently ${resolved}`
              : option.label;
          return html`
            <button
              type="button"
              role="radio"
              aria-checked=${checked ? "true" : "false"}
              aria-label=${title}
              title=${title}
              tabindex=${checked ? "0" : "-1"}
              @click=${() => this.select(option.value)}
            >
              <svg viewBox="0 0 16 16" aria-hidden="true" focusable="false">
                <path d=${option.path} fill="currentColor" />
              </svg>
            </button>
          `;
        })}
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "sluice-theme-toggle": SluiceThemeToggle;
  }
}
